import { apiGet, apiPost } from './apiClient';

const formatCurrency = (v) => {
  if (v == null) return '—';
  const n = typeof v === 'number' ? v : parseFloat(v);
  return isNaN(n) ? '—' : `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercent = (v) => {
  if (v == null) return '—';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (isNaN(n)) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
};

const rsFieldByPeriod = {
  '1d': 'day1d',
  '1w': 'week1w',
  '1m': 'month1m',
  '3m': 'month3m',
  '6m': 'month6m',
  '1y': 'year1y',
  '3y': 'year3y',
};

/** True when row was already passed through mapStockToTable (no raw API fields). */
const isMappedStockRow = (s) =>
  s != null
  && typeof s === 'object'
  && (Object.prototype.hasOwnProperty.call(s, 'avgVolume')
    || Object.prototype.hasOwnProperty.call(s, 'cmp')
    || Object.prototype.hasOwnProperty.call(s, 'subSector'))
  && s.avg_volume == null
  && s.price == null
  && s.market_cap == null
  && s.subsector == null;

const mapStockToTable = (s, idx, opts = {}) => {
  // Idempotent: remapping a cached mapped row must not wipe CMP / Avg Vol / etc.
  if (isMappedStockRow(s)) {
    return {
      ...s,
      id: s.id || String(idx + 1).padStart(2, '0'),
    };
  }

  const period = opts.period || '1d';
  const perfField = rsFieldByPeriod[period] || 'day1d';
  // Do not fall back to 1D when selected horizon data is unavailable.
  // Showing day1d for 1w/6m makes the Alpha Tracker look like all "+0.00%".
  const chgVal = s[perfField];

  const volJump = (s.volume && s.avg_volume && s.avg_volume > 0)
    ? (s.volume / s.avg_volume).toFixed(1) + 'x'
    : '—';

  // RS% = relative strength vs benchmark (API). Never mix with price % or RSI.
  const rsDisplay =
    s.relative_strength != null && !Number.isNaN(Number(s.relative_strength))
      ? formatPercent(s.relative_strength)
      : '—';

  // RSI oscillator 0–100 from TechnicalSignal / Wilder fallback (Alpha Tracker & APIs that send rsi).
  const rsiDisplay =
    s.rsi != null && !Number.isNaN(Number(s.rsi))
      ? Number(s.rsi).toFixed(1)
      : '—';

  return {
    id: String(idx + 1).padStart(2, '0'),
    symbol: s.symbol,
    sector: s.sector || '—',
    subSector: s.subsector || '—',
    mc: s.market_cap || '—',
    ema21: formatCurrency(s.ema21),
    ema50: formatCurrency(s.ema50),
    cmp: formatCurrency(s.price),
    chg: formatPercent(chgVal),
    rs: rsDisplay,
    rsi: rsiDisplay,
    volume: s.volume != null ? Number(s.volume).toLocaleString('en-IN') : '—',
    avgVolume: s.avg_volume != null ? Math.round(s.avg_volume).toLocaleString('en-IN') : '—',
    volJump,
    date: s.last_updated,
  };
};

export const mapStockListToTable = (list, opts = {}) =>
  (list || []).map((s, i) => mapStockToTable(s, i, opts));

const volChgFieldMap = { day: 'percent_change_volume_1d', week: 'percent_change_volume_1w', month: 'percent_change_volume_1m' };
const priceChgFieldMap = { day: 'day1d', week: 'week1w', month: 'month1m' };
const priceShockersChgFieldMap = { day: 'day1d', week: 'week1w', month: 'month1m' };

export const mapVolumeShockersList = (list, period = 'day') => {
  const volChgField = volChgFieldMap[period] || 'percent_change_volume_1d';
  const priceChgField = priceChgFieldMap[period] || 'day1d';
  return (list || []).map((s, i) => {
    const base = mapStockToTable(s, i, {});
    if (isMappedStockRow(s) && (s.volChgPct != null || s.chg != null)) {
      return {
        ...base,
        volChgPct: s.volChgPct ?? base.volChgPct ?? '—',
        volChgRaw: s.volChgRaw,
        chg: s.chg ?? base.chg,
        chgRaw: s.chgRaw,
      };
    }
    return {
      ...base,
      volChgPct: s[volChgField] != null ? `${s[volChgField] >= 0 ? '+' : ''}${Number(s[volChgField]).toFixed(1)}%` : '—',
      volChgRaw: s[volChgField],
      chg: formatPercent(s[priceChgField]),
      chgRaw: s[priceChgField],
    };
  });
};

export const mapPriceShockersList = (list, period = 'day') => {
  const chgField = priceShockersChgFieldMap[period] || 'day1d';
  return (list || []).map((s, i) => {
    const base = mapStockToTable(s, i, {});
    if (isMappedStockRow(s) && s.chg != null) {
      return { ...base, chg: s.chg };
    }
    return {
      ...base,
      chg: formatPercent(s[chgField]),
    };
  });
};

export const fetchRelativePerformanceRaw = async (period = '1d', limit = 50, dateStr = null) => {
  let url = `/stocks/relative-performance?period=${period}&limit=${limit}`;
  if (dateStr) url += `&date=${dateStr}`;
  const data = await apiGet(url);
  return data?.data ?? [];
};

export const fetchVolumeShockersRaw = async (limit = 50, period = 'day', dateStr = null) => {
  let url = `/stocks/volume-shockers?limit=${limit}&period=${period}`;
  if (dateStr) url += `&date=${dateStr}`;
  const data = await apiGet(url);
  return data?.data ?? [];
};

export const fetchPriceShockersRaw = async (type = 'gainers', limit = 50, period = 'day', dateStr = null) => {
  let url = `/stocks/price-shockers?type=${type}&limit=${limit}&period=${period}`;
  if (dateStr) url += `&date=${dateStr}`;
  const data = await apiGet(url);
  return data?.data ?? [];
};

export const fetchRelativePerformance = async (period = '1d', limit = 50, dateStr = null) => {
  const list = await fetchRelativePerformanceRaw(period, limit, dateStr);
  return mapStockListToTable(list, { period });
};

export const fetchVolumeShockers = async (limit = 50, period = 'day', dateStr = null) => {
  const list = await fetchVolumeShockersRaw(limit, period, dateStr);
  return mapVolumeShockersList(list, period);
};

export const fetchPriceShockers = async (type = 'gainers', limit = 50, period = 'day', dateStr = null) => {
  const list = await fetchPriceShockersRaw(type, limit, period, dateStr);
  return mapPriceShockersList(list, period);
};

export const fetchTrendingRaw = async (limit = 50, dateStr = null) => {
  let url = `/stocks/trending?limit=${limit}`;
  if (dateStr) url += `&date=${dateStr}`;
  const data = await apiGet(url);
  return data?.data ?? [];
};

export const fetchTrending = async (limit = 50, dateStr = null) => {
  const list = await fetchTrendingRaw(limit, dateStr);
  return mapStockListToTable(list, {});
};

export const fetchScreenDates = async () => {
  const data = await apiGet('/stocks/screen-dates');
  return data?.dates ?? [];
};

export const fetchIPOs = async (status = null, limit = 100) => {
  let url = `/ipo?limit=${limit}`;
  if (status) url += `&status=${encodeURIComponent(status)}`;
  const data = await apiGet(url);
  return data?.data ?? [];
};

export const fetchStocksBySubsector = async (subsector, limit = 200) => {
  try {
    // Try the dedicated endpoint first
    console.log('Trying /stocks/by-subsector endpoint...');
    const data = await apiGet(`/stocks/by-subsector?subsector=${encodeURIComponent(subsector)}&limit=${limit}`);
    const list = data?.data ?? [];
    console.log('Success: got', list.length, 'stocks from /stocks/by-subsector');
    return list.map((s, i) => mapStockToTable(s, i, {}));
  } catch (err) {
    console.warn(`/stocks/by-subsector failed:`, err?.message);
  }
  
  try {
    // Try generic stocks endpoint with subsector parameter
    console.log('Trying /stocks endpoint with subsector param...');
    const data = await apiGet(`/stocks?subsector=${encodeURIComponent(subsector)}&limit=${limit}`);
    const list = data?.data ?? [];
    console.log('Success: got', list.length, 'stocks from /stocks');
    return list.map((s, i) => mapStockToTable(s, i, {}));
  } catch (err) {
    console.warn(`/stocks with subsector failed:`, err?.message);
  }

  try {
    // Try fetching all stocks and filtering client-side
    console.log('Trying to fetch all stocks from /stocks/all...');
    const data = await apiGet(`/stocks/all?limit=${limit}`);
    const list = data?.data ?? [];
    console.log('Got', list.length, 'total stocks, filtering by subsector...');
    const filtered = list.filter(s => 
      s.subsector && s.subsector.toLowerCase().trim() === subsector.toLowerCase().trim()
    );
    console.log('Filtered to', filtered.length, 'stocks');
    return filtered.map((s, i) => mapStockToTable(s, i, {}));
  } catch (err) {
    console.warn(`/stocks/all failed:`, err?.message);
  }

  console.warn(`All subsector endpoints failed for "${subsector}"`);
  return [];
};

export const fetchWeeklyPicks = async () => {
  return apiGet('/stocks/weekly-picks');
};

export const triggerWeeklyPicks = async () => {
  return apiPost('/stocks/weekly-picks/run');
};

export const generateWeeklyPicks = async () => {
  return apiPost('/stocks/weekly-picks/generate');
};
